import React from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal, Pop } from '../ui';
import { Footprints, Shirt, Watch, Bike, CupSoda, Backpack, Package, BellRing, Flame, Tag } from 'lucide-react';
import { kitCategoryLabel, formatListingPrice, isNumericPrice, numericPriceValue } from '../../lib/kit';

const ICON = { shoes: Footprints, apparel: Shirt, tech: Watch, cycling: Bike, hydration: CupSoda, bag: Backpack, other: Package };
function KitIcon({ cat }) { const I = ICON[cat] || Package; return <I size={38} strokeWidth={1.75} />; }

function StatusChip({ status }) {
  if (!status || status === 'available') return null;
  const map = { reserved: ['status-chip reserved', 'Reserved'], sold: ['status-chip sold', 'Sold'] };
  const [cls, label] = map[status] || [];
  return cls ? <span className={cls} data-testid={`status-chip-${status}`}>{label}</span> : null;
}

export function ListingPrice({ l }) {
  if (l.oldPrice) {
    return (
      <div className="price price-reduced" data-testid={`listing-price-${l.id}`}>
        <span className="price-was">{formatListingPrice(l.oldPrice)}</span>
        <span className="price-now">{formatListingPrice(l.price)}</span>
      </div>
    );
  }
  return <div className="price" data-testid={`listing-price-${l.id}`}>{formatListingPrice(l.price)}</div>;
}

function ReducePrice({ l, reduceListingPrice }) {
  const [open, setOpen] = React.useState(false);
  const [val, setVal] = React.useState('');
  if (!isNumericPrice(l.price)) return null;
  const submit = () => {
    const amt = parseFloat(val);
    if (!amt || amt <= 0 || amt >= numericPriceValue(l.price)) return;
    reduceListingPrice(l.id, val);
    setOpen(false);
    setVal('');
  };
  return open ? (
    <div className="offer-form" data-testid={`reduce-price-form-${l.id}`}>
      <span className="offer-currency">£</span>
      <input type="number" min="0.01" step="0.01" max={numericPriceValue(l.price)} placeholder="New price" value={val}
        data-testid={`reduce-price-input-${l.id}`} onChange={(e) => setVal(e.target.value)} />
      <button className="btn secondary" data-testid={`reduce-price-submit-${l.id}`} onClick={submit}>Save</button>
    </div>
  ) : (
    <button className="btn ghost" data-testid={`reduce-price-btn-${l.id}`} onClick={() => setOpen(true)}><Tag size={15} /> Reduce price</button>
  );
}

function SellerControls({ l, setListingStatus, reduceListingPrice }) {
  return (
    <div className="seller-controls" data-testid={`seller-controls-${l.id}`}>
      {l.status !== 'available' && <button className="btn ghost" onClick={() => setListingStatus(l.id, 'available')}>Available</button>}
      {l.status !== 'reserved' && <button className="btn secondary" onClick={() => setListingStatus(l.id, 'reserved')}>Reserve</button>}
      {l.status !== 'sold' && <button className="btn" onClick={() => setListingStatus(l.id, 'sold')}>Mark sold</button>}
      {l.status === 'available' && <ReducePrice l={l} reduceListingPrice={reduceListingPrice} />}
    </div>
  );
}

export default function Sell() {
  const { state, role, user, openModal, removeListing, notifyInterest, setListingStatus, reduceListingPrice } = useSpc();
  return (
    <>
      <Reveal className="section-head">
        <div><div className="eyebrow">Community</div><h2>Buy &amp; Sell</h2><p>Buy or sell useful kit within the SPC member community.</p></div>
        {role === 'member' && <button className="btn" data-testid="sell-kit-btn" onClick={() => openModal('newListing')}>Sell kit</button>}
      </Reveal>

      {role === 'member' && (
        <Reveal delay={0.03} className="card accent">
          <span className="pill yellow">HOW IT WORKS</span>
          <div className="how-grid">
            <div className="how-step"><span className="how-num">1</span><div><strong>List your kit</strong><p>Post an item to sell in seconds.</p></div></div>
            <div className="how-step"><span className="how-num">2</span><div><strong>Get notified</strong><p>We ping you the moment a member's interested.</p></div></div>
            <div className="how-step"><span className="how-num">3</span><div><strong>Arrange it</strong><p>Chat and sort the sale directly with the member.</p></div></div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.05} className="card">
        <h3>SPC Marketplace</h3><p>Sell unwanted running, cycling, triathlon and training kit within the SPC community. Tap <strong style={{ color: '#fff' }}>I'm interested</strong> and the seller gets an instant notification and a chat.</p>
        <div className="notice" data-testid="sell-listing-guidelines-note">A quick note about selling on the Club App… Please only list items relevant to sport, training or the SPC community. We don't need your old washing machine or MOT-failed car — Facebook Marketplace can have those! 😂</div>
        <div className="market-grid" style={{ marginTop: 18 }}>
          {state.listings.length === 0 && (
            <div className="card span-12"><div className="empty" data-testid="market-empty"><Package size={26} /><p>No listings yet — be the first to post some kit.</p></div></div>
          )}
          {state.listings.map((l, i) => {
            const cat = l.category || 'other';
            const mine = user && l.sellerId === user.id;
            const sold = l.status === 'sold';
            return (
              <Pop className={`product ${sold ? 'is-sold' : ''}`} key={l.id} delay={Math.min(i * 0.05, 0.3)} data-testid={`listing-${l.id}`}>
                <div className="product-img" onClick={() => openModal('listing', { id: l.id })} style={{ cursor: 'pointer' }}>
                  {(l.photos && l.photos.length)
                    ? <img className="product-photo" src={l.photos[0]} alt={l.title} />
                    : <span className="product-icon"><KitIcon cat={cat} /></span>}
                  <span className="product-cat-chip">{kitCategoryLabel(cat)}</span>
                  <StatusChip status={l.status} />
                  {l.photoCount > 1 && <span className="product-count">{l.photoCount} photos</span>}
                </div>
                <div className="product-body">
                  <div className="product-tags"><span className="pill">{l.condition}</span>{mine && <span className="pill yellow">Your listing</span>}</div>
                  <h4 onClick={() => openModal('listing', { id: l.id })} style={{ cursor: 'pointer' }} data-testid={`listing-title-${l.id}`}>{l.title}</h4>
                  <ListingPrice l={l} />
                  <div className="meta">Listed by {l.seller} · {l.listedAgo}</div>
                  {l.interestCount > 0 && (
                    <div className="pill soft" data-testid={`interest-count-${l.id}`}>
                      <Flame size={11} /> {l.interestCount} {l.interestCount === 1 ? 'person' : 'people'} interested
                    </div>
                  )}
                  {role === 'admin'
                    ? <button className="btn danger" onClick={() => removeListing(l.id)}>Remove</button>
                    : mine
                      ? <SellerControls l={l} setListingStatus={setListingStatus} reduceListingPrice={reduceListingPrice} />
                      : <button className="btn secondary" data-testid={`interest-btn-${l.id}`} disabled={sold} onClick={() => notifyInterest(l)}><BellRing size={15} /> {sold ? 'Sold' : "I'm interested"}</button>}
                </div>
              </Pop>
            );
          })}
        </div>
      </Reveal>
    </>
  );
}
